import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropertiesPanel from '../properties/PropertiesPanel';
import Depths3DPanel from '../properties/Depths3DPanel';
import ValidationPanel from '../validation/ValidationPanel';
import LayersPanel from '../layers/LayersPanel';
import DxfLayersPanel from '../layers/DxfLayersPanel';
import FacilityPanel from '../layers/FacilityPanel';
import EquipmentLibraryPanel from '../equipment/EquipmentLibraryPanel';
import ElementsPanel from '../elements/ElementsPanel';
import CanvasMinimap from '../CanvasMinimap/CanvasMinimap';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setRightDrawerTab, toggleRightSidebar } from '../../features/ui/uiSlice';
import type { UIState } from '../../types';

type DrawerTab = UIState['rightDrawerTab'];

type TabDef = {
  id: DrawerTab;
  label: string;
  icon: React.ReactNode;
};

const RightPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector((state) => state.ui.rightSidebarOpen);
  const rightDrawerTab = useAppSelector((state) => state.ui.rightDrawerTab);
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);

  const tabs: TabDef[] = useMemo(
    () => [
      { id: 'properties', label: 'Properties', icon: <PropsIcon /> },
      { id: 'dxf-layers', label: 'DXF Layers', icon: <DxfLayersIcon /> },
      { id: 'layers', label: 'Layers', icon: <LayersIcon /> },
      ...(!view3dOpen
        ? [{ id: 'equipment' as const, label: 'Equipment', icon: <EquipmentIcon /> }]
        : [{ id: '3ddepths' as const, label: '3D Depths', icon: <DepthsIcon /> }]),
      { id: 'elements', label: 'Elements', icon: <ElementsIcon /> },
    ],
    [view3dOpen]
  );

  const tabById = useMemo(() => Object.fromEntries(tabs.map((t) => [t.id, t])), [tabs]);

  /** Two visible tab slots; overflow picks always replace slot 2. */
  const [visibleIds, setVisibleIds] = useState<[DrawerTab, DrawerTab]>([
    'properties',
    'dxf-layers',
  ]);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  // Keep slots valid when available tabs change (e.g. 2D ↔ 3D).
  useEffect(() => {
    const valid = new Set(tabs.map((t) => t.id));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleIds((prev) => {
      let [first, second] = prev;
      if (!valid.has(first)) first = tabs[0]?.id ?? 'properties';
      if (!valid.has(second) || second === first) {
        second = tabs.find((t) => t.id !== first)?.id ?? first;
      }
      return [first, second];
    });
  }, [tabs]);

  // If something else activates a hidden tab, promote it into slot 2.
  useEffect(() => {
    if (!tabs.some((t) => t.id === rightDrawerTab)) return;
    if (visibleIds.includes(rightDrawerTab)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleIds(([first]) => [first, rightDrawerTab]);
  }, [rightDrawerTab, tabs, visibleIds]);

  useEffect(() => {
    if (!overflowOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [overflowOpen]);

  const selectTab = (id: DrawerTab) => {
    dispatch(setRightDrawerTab(id));
    if (!expanded) {
      dispatch(toggleRightSidebar());
    }
  };

  const selectFromOverflow = (id: DrawerTab) => {
    setVisibleIds(([first]) => [first, id]);
    dispatch(setRightDrawerTab(id));
    setOverflowOpen(false);
  };

  const visibleTabs = visibleIds.map((id) => tabById[id]).filter((t): t is TabDef => Boolean(t));

  const overflowTabs = tabs.filter((t) => !visibleIds.includes(t.id));

  if (!expanded) {
    return (
      <aside
        className="absolute top-3 right-3 z-20 w-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col items-center py-3 gap-1"
        aria-label="Right panel (collapsed)"
      >
        <button
          type="button"
          onClick={() => dispatch(toggleRightSidebar())}
          title="Expand panel"
          aria-label="Expand panel"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
        >
          <ChevronLeftIcon />
        </button>

        <div className="w-8 border-t border-slate-200 my-1" />

        <div className="flex flex-col items-center gap-1">
          {tabs.map((tab) => (
            <CollapsedTabButton
              key={tab.id}
              active={rightDrawerTab === tab.id}
              label={tab.label}
              onClick={() => selectTab(tab.id)}
            >
              {tab.icon}
            </CollapsedTabButton>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="absolute top-3 right-3 bottom-3 z-20 w-80 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col min-w-0 overflow-hidden"
      aria-label="Right panel"
    >
      {/* Header: collapse + two tabs + overflow */}
      <div className="shrink-0 border-b border-slate-200 px-2 pt-2">
        <div className="flex items-center gap-0.5 min-w-0">
          <button
            type="button"
            onClick={() => dispatch(toggleRightSidebar())}
            title="Collapse panel"
            aria-label="Collapse panel"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <ChevronRightIcon />
          </button>

          {visibleTabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={rightDrawerTab === tab.id}
              label={tab.label}
              onClick={() => dispatch(setRightDrawerTab(tab.id))}
            >
              {tab.icon}
            </TabButton>
          ))}

          {overflowTabs.length > 0 && (
            <div ref={overflowRef} className="relative shrink-0 ml-auto">
              <button
                type="button"
                onClick={() => setOverflowOpen((o) => !o)}
                title="More tabs"
                aria-label="More tabs"
                aria-expanded={overflowOpen}
                className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                  overflowOpen
                    ? 'bg-slate-100 text-slate-700'
                    : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                }`}
              >
                <OverflowChevronsIcon />
              </button>
              {overflowOpen && (
                <div className="absolute right-0 top-full mt-1 min-w-[160px] rounded-lg border border-slate-200 bg-white shadow-lg py-1 z-50">
                  {overflowTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => selectFromOverflow(tab.id)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                    >
                      <span className="text-slate-400">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto scrollbar-thin">
        {rightDrawerTab === 'properties' && (
          <>
            <PropertiesPanel />
            <ValidationPanel />
          </>
        )}
        {rightDrawerTab === 'dxf-layers' && <DxfLayersPanel />}
        {rightDrawerTab === 'layers' && <LayersPanel />}
        {rightDrawerTab === 'facility' && <FacilityPanel />}
        {rightDrawerTab === 'equipment' && <EquipmentLibraryPanel />}
        {rightDrawerTab === 'elements' && <ElementsPanel />}
        {rightDrawerTab === '3ddepths' && view3dOpen && <Depths3DPanel />}
      </div>

      <div className="shrink-0 border-t border-slate-200">
        <CanvasMinimap />
      </div>
    </aside>
  );
};

const TabButton: React.FC<{
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    className={`inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors min-w-0 ${
      active
        ? 'border-primary-600 text-primary-600'
        : 'border-transparent text-slate-500 hover:text-slate-700'
    }`}
  >
    <span className={`shrink-0 ${active ? 'text-primary-600' : 'text-slate-400'}`}>{children}</span>
    <span className="truncate">{label}</span>
  </button>
);

const CollapsedTabButton: React.FC<{
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
      active
        ? 'bg-primary-50 text-primary-600'
        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

const iconClass = 'w-[18px] h-[18px] shrink-0';

const OverflowChevronsIcon: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M5 4.5 8.5 8 5 11.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 4.5 12.5 8 9 11.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M6 3.5 10.5 8 6 12.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M10 3.5 5.5 8 10 12.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PropsIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M3 5.5h12M3 9h12M3 12.5h12" strokeLinecap="round" />
    <circle cx="7" cy="5.5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="11" cy="9" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="6" cy="12.5" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

const DxfLayersIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M3 5.5 9 2.5 15 5.5 9 8.5 3 5.5Z" strokeLinejoin="round" />
    <path d="M3 9.5 9 12.5 15 9.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 12.5 9 15.5 15 12.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LayersIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M3 7.5 9 4.5 15 7.5 9 10.5 3 7.5Z" strokeLinejoin="round" />
    <path d="M3 10.5 9 13.5 15 10.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EquipmentIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <rect x="3.5" y="5" width="11" height="9" rx="1.5" />
    <path d="M6.5 5V3.5h5V5M7 9.5h4" strokeLinecap="round" />
  </svg>
);

const ElementsIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <rect x="3" y="3" width="4.5" height="4.5" rx="1" />
    <rect x="10.5" y="3" width="4.5" height="4.5" rx="1" />
    <rect x="3" y="10.5" width="4.5" height="4.5" rx="1" />
    <rect x="10.5" y="10.5" width="4.5" height="4.5" rx="1" />
  </svg>
);

const DepthsIcon: React.FC = () => (
  <svg
    className={iconClass}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M3 13.5 9 4.5 15 13.5H3Z" strokeLinejoin="round" />
    <path d="M6 13.5 9 8.5 12 13.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default RightPanel;
